<template>
  <div class="room" v-show="showFloating">
    <b-collapse class="card" animation="slide" :open="false">
      <template #trigger="props">
        <div class="card-header room__header">
          <p class="has-text-white">
            <StatusSelector :large="true" />
          </p>

          <a class="has-text-success" role="button">
            <b-icon
              :icon="
                activePlayers.length > 2
                  ? 'users'
                  : activePlayers.length === 2
                  ? 'user-friends'
                  : activePlayers.length == 1
                  ? 'user-astronaut'
                  : 'user'
              "
            />
            &nbsp;{{ otherPlayers.length }}
            <b-icon :icon="props.open ? 'caret-down' : 'caret-up'"></b-icon>
          </a>
        </div>
      </template>

      <div class="table__container">
        <!-- Search box -->
        <b-field class="my-0">
          <b-input
            v-model="filterText"
            placeholder="Type to search players"
            type="search"
            icon="search"
          ></b-input>
        </b-field>

        <table class="room__players">
          <tbody>
            <tr v-for="player in availablePlayers" :key="player.id">
              <td
                @mouseover="hoveredId = player.user_id"
                @mouseleave="hoveredId = null"
              >
                <ProfileCard
                  :player="player"
                  :rating="player.stats ? player.stats.rating : null"
                  :onlinePlayer="player"
                  :challenge="!currentMatch"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-collapse>

    <div>
      <ChallengeSentModal
        v-if="challengedPlayer && challengedPlayer.challenge"
        :player="challengedPlayer"
        :duration="20"
        @onCancelChallenge="cancelChallenge"
      />
      <ChallengeReceivedModal
        v-else-if="receivedChallenge && receivedChallenge.player"
        :challenge="receivedChallenge"
        :duration="15"
        @onDenyChallenge="denyChallenge"
        @onAcceptChallenge="acceptChallenge"
      />
    </div>
    <div v-if="currentPlayer && currentPlayer.username">
      <PersonalNotification :username="currentPlayer.username" />
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import ProfileCard from "@/components/players/ProfileCard.vue";
import StatusSelector from "@/components/statuses/StatusSelector.vue";
import PersonalNotification from "@/components/home/PersonalNotification.vue";
import RoomRenderlessVue from "./RoomRenderless.vue";

import { mapGetters, mapMutations } from "vuex";
import { EventBus } from "@/services/event-bus";
import { firestore } from "@/services/firebase";
import { ONLINE_STATUS } from "@/constants";
import { MatchesAPI } from "@/services/api";
import gameUrls from "@/mixins/gameUrls";
import { cachedTimeOffset } from "@/utils/firebase-helper";

export default {
  name: "RoomFloating",

  components: {
    ProfileCard,
    StatusSelector,
    PersonalNotification,
  },

  mixins: [gameUrls],

  extends: RoomRenderlessVue,

  data() {
    return {
      hoveredId: null,
      timerInterval: null,
      filterText: "",
      liveMatches: [],
    };
  },

  computed: {
    ...mapGetters({
      userId: "getUserId",
      currentPlayer: "getPlayer",
      currentPlayerStats: "getStats",
    }),

    // if there is an open or live match, then watch the match and automatically back to it in a watch
    // except for the simul match because users are allowed to play other games
    // as long as they are not hosting a any simul
    currentMatch() {
      if (!this.liveMatches) return null;
      return this.liveMatches.find(
        (m) =>
          !m.result &&
          ((m.player1 && m.player1.user_id === this.userId) ||
            (m.player2 && m.player2.user_id === this.userId))
      );
    },

    matchId() {
      return this.currentMatch ? this.currentMatch.match_id : null;
    },

    pageName() {
      return this.$route.name;
    },

    showFloating() {
      return (
        this.pageName !== "game" &&
        this.pageName !== "gameV2" &&
        this.pageName !== "tournament" &&
        this.pageName !== "tournamentGame" &&
        this.pageName !== "tournamentGameV2" &&
        this.pageName !== "simulGames" &&
        !this.pageName?.includes("Examples") &&
        !this.pageName?.includes("lecture")
      );
    },

    availablePlayers() {
      if (!this.otherPlayers || !this.otherPlayers.length) return [];
      let filtered = this.otherPlayers
        .filter((player) =>
          [
            ONLINE_STATUS.ACTIVE,
            ONLINE_STATUS.AWAY,
            ONLINE_STATUS.BUSY,
            ONLINE_STATUS.PLAYING,
          ].includes(player.status)
        )
        .sort((p1, p2) =>
          p1.status > p2.status ? 1 : p1.status < p2.status ? -1 : 0
        );
      if (this.filterText) {
        filtered = filtered.filter((player) =>
          player.name.toLowerCase().includes(this.filterText.toLowerCase())
        );
      }
      return filtered;
    },

    activePlayers() {
      if (!this.otherPlayers || !this.otherPlayers.length) return [];
      return this.otherPlayers.filter(
        (player) => player.status === ONLINE_STATUS.ACTIVE
      );
    },

    shouldRedirectToGame() {
      if (!this.currentMatch || !this.currentMatch.startedAt) return false;

      // Redirect to game page if match is live
      // Tournament match is handled by TournamentFloating
      return (
        this.currentMatch.status === "live" &&
        !this.currentMatch.result &&
        !this.currentMatch.tournamentId &&
        this.pageName !== "game" &&
        this.pageName !== "gameV2" &&
        this.pageName !== "tournamentGame" &&
        this.pageName !== "tournamentGameV2" &&
        this.pageName !== "simulGames"
      );
    },

    username() {
      return this.currentPlayer ? this.currentPlayer.username : null;
    },
  },

  watch: {
    otherPlayers: function () {
      this.setOnlinePlayers(this.otherPlayers);
    },

    currentMatch: {
      immediate: true,
      deep: true,
      handler() {
        this.checkCurrentMatch();
      },
    },

    username() {
      if (!this.username) {
        return;
      }

      if (this.currentPlayerStats) return;

      MatchesAPI.getPlayerRating(this.currentPlayer.username).then((data) => {
        this.setStats(data);
      });
    },

    pageName() {
      this.checkCurrentMatch();
    },
  },

  methods: {
    ...mapMutations(["setOnlinePlayers"]),
    ...mapMutations(["setStats"]),

    async checkCurrentMatch() {
      // check tournament game and redirect
      if (
        this.currentMatch &&
        this.currentMatch.tournamentId &&
        this.currentMatch.tournamentId != this.tournamentId
      ) {
        this.$router.push({
          path: this.getTournamentGameUrl(
            this.currentMatch.tournamentId,
            this.currentMatch.match_id
          ),
          query: this.$route.query,
        });
        return;
      }

      if (!this.shouldRedirectToGame) {
        return;
      }

      // with simul game, because player is automatically added to simul game to start without joining
      // so no time offset is given when host starts simul games
      // we need to update time offset when user is redirected to simul game to play
      // for normal games, it has already been updated, but it could be updated here as well, so no problem
      const matchRef = firestore
        .collection("matches")
        .doc(this.currentMatch.match_id);
      if (
        this.currentMatch.player1 &&
        this.currentMatch.player1.user_id === this.userId
      ) {
        await matchRef.update({
          player1: {
            ...this.currentMatch.player1,
            timeOffset: cachedTimeOffset(),
          },
        });
      } else {
        await matchRef.update({
          player2: {
            ...this.currentMatch.player2,
            timeOffset: cachedTimeOffset(),
          },
        });
      }

      // check normal game/simul games and redirect
      const simulId = this.currentMatch.simulId;
      if (simulId) {
        const simul = await firestore.collection("simuls").doc(simulId).get();
        const simulData = simul.data();

        if (!simulData || !simulData.owner || !this.currentPlayer) {
          this.goToMatch(this.currentMatch);
          return;
        }

        if (simulData.owner.user_id !== this.currentPlayer.user_id) {
          // Go to single game if current user is not the host
          this.goToMatch(this.currentMatch);
          return;
        }

        // Go to simul games if current user is the host
        this.goToSimul(simulId);
      } else {
        this.goToMatch(this.currentMatch);
      }
    },

    goToMatch: _.debounce(function (match) {
      console.log("goToMatch", match.id);
      this.$router.push({
        path: this.getGameUrl(match.id),
      });
    }, 500),

    goToSimul: _.debounce(function (simulId) {
      console.log("goToSimul", simulId);
      if (this.$route.name === "simul") {
        return;
      }
      this.$router.push({
        path: `/simuls/${simulId}`,
      });
    }, 500),
  },

  created() {
    this.$bind(
      "liveMatches",
      firestore.collection("matches").where("status", "==", "live")
    );
  },

  mounted() {
    EventBus.$on("onChallenge", this.promptDuration);
    EventBus.$on("onRematch", this.sendChallenge);
  },

  beforeDestroy() {
    EventBus.$off("onChallenge", this.promptDuration);
    EventBus.$off("onRematch", this.sendChallenge);
  },
};
</script>

<style scoped>
.room {
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 1000;
  min-width: 300px;
}

.table__container {
  max-height: 75vh;
  overflow: scroll;
  overflow-x: hidden;
}

.room__header {
  background-color: darkslateblue; /*  #29c82d; */
  position: sticky;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

table {
  background-color: #e9ffe8;
  width: 100%;
}

tbody tr td {
  width: 100%;
}

td {
  padding: 10px 5px;
}
</style>
